import { getBackendBaseUrl, getLoginPath } from './config';
import { clearAuthToken, getAuthHeaders } from './auth';

export interface CardToIbanResult {
  card_number: string;
  iban: string;
  owner?: string;
  bank_name?: string;
  bank_code?: string;
  sms_ttl?: number | null;
}

export interface CardToIbanStatus {
  configured: boolean;
  auto_login_configured?: boolean;
  auto_refresh_enabled?: boolean;
  session_valid?: boolean;
  token_expires_at?: string | null;
  last_refresh_at?: string | null;
}

export interface MissingIbanItem {
  id: number;
  employee_id: number;
  employee_name: string;
  personnel_code?: string | null;
  bank_id?: number | null;
  bank_name?: string | null;
  account_holder_name?: string | null;
  card_number: string;
  card_masked: string;
  iban?: string | null;
  is_default?: boolean;
}

export interface FillIbanResult {
  id: number;
  employee_id?: number;
  employee_name?: string;
  iban?: string;
  owner?: string | null;
  bank_name?: string | null;
  bank_code?: string | null;
  card_number?: string;
  already_had_iban?: boolean;
}

function resolveUrl(path: string): string {
  const base = getBackendBaseUrl();
  if (path.startsWith('/employees/')) {
    const root = base ? `${base}/api/panel/accounting` : '/api/panel/accounting';
    return `${root}${path}`;
  }
  const root = base ? `${base}/api` : '/api';
  return `${root}${path}`;
}

async function fetchApi<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(resolveUrl(path), {
    ...init,
    headers: {
      ...getAuthHeaders(),
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 401 && typeof window !== 'undefined') {
    clearAuthToken();
    window.location.href = getLoginPath();
    return new Promise(() => {});
  }

  const json = (await res.json().catch(() => ({}))) as T & {
    success?: boolean;
    message?: string;
  };

  if (!res.ok || json.success === false) {
    throw new Error(json.message ?? 'خطا در ارتباط با سرویس کارت به شبا');
  }

  return json;
}

export const cardToIbanApi = {
  status: () =>
    fetchApi<{ success: boolean; data: CardToIbanStatus }>('/tools/card-to-iban/status'),

  refreshSession: () =>
    fetchApi<{ success: boolean; message: string; data?: CardToIbanStatus }>('/tools/card-to-iban/refresh', {
      method: 'POST',
    }),

  updateToken: (credentials: string) =>
    fetchApi<{ success: boolean; message: string }>('/tools/card-to-iban/token', {
      method: 'POST',
      body: JSON.stringify({ credentials }),
    }),

  convert: (cardNumber: string) =>
    fetchApi<{ success: boolean; data: CardToIbanResult; message?: string }>('/tools/card-to-iban/convert', {
      method: 'POST',
      body: JSON.stringify({ card_number: cardNumber }),
    }),

  missingIban: () =>
    fetchApi<{ success: boolean; data: { count: number; items: MissingIbanItem[] } }>(
      '/tools/card-to-iban/missing-iban',
    ),

  fillIban: (accountId: number) =>
    fetchApi<{ success: boolean; message: string; data?: FillIbanResult }>(
      `/tools/card-to-iban/fill-iban/${accountId}`,
      { method: 'POST' },
    ),

  /** استعلام بانک/شبا/نام از شماره کارت (برای فرم حساب بانکی) */
  lookupFromCard: (cardNumber: string) =>
    fetchApi<{
      success: boolean;
      message?: string;
      data: {
        card_number: string;
        iban: string;
        account_holder_name?: string;
        bank_name?: string;
        bank_id?: number | null;
        bank_code?: string;
      };
    }>('/employees/get-bank-info-from-card', {
      method: 'POST',
      body: JSON.stringify({ card_number: cardNumber }),
    }),
};
